// ════════════════════════════════════════════════════════════════════════════
// M1.5 Madurez — panel "Análisis IA" (prompt 0729 F6.4)
// Captura cualitativa (conclusión del consultor) + generación IA on-demand +
// diagnóstico narrativo + recomendaciones ancladas (badge) + alertas Sentinel +
// validación human-in-the-loop. Consume /structure/madurez/* (F6.3).
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../../../i18n';
import { mensajeErrorIA } from './madurez-error';
import {
  BASE,
  getToken,
  obtenerAnalisisMadurez,
  generarAnalisisMadurez,
  validarAnalisisMadurez,
  descargarExportadorPyMadurez,
  descargarMegapromptMadurez,
  obtenerInformeHtmlMadurez,
  type AnalisisMadurez,
  type DimensionMadurez,
  type NivelMadurez,
  type FuenteAnalisisMadurez,
} from '../../../../shared/services/api';

import {
  COLORS,
  SectionCard,
  Badge,
  Markdown,
  ListaPunteada,
  DAFOMatrix,
  type DafoData,
} from './analisis-ui';

const FUENTES: FuenteAnalisisMadurez[] = ['CUESTIONARIO', 'REAL', 'AMBAS'];

/** Mapa cuadrante PICK → estilo del badge (0740). */
const PICK_BADGE: Record<string, { tono: 'verde' | 'ambar' | 'azul' | 'neutro'; label: string }> = {
  QUICK_WIN: { tono: 'verde', label: 'Quick win' },
  RETO: { tono: 'ambar', label: 'Reto' },
  POSIBLE: { tono: 'azul', label: 'Posible' },
  DESCARTAR: { tono: 'neutro', label: 'Descartar' },
};

/** Lectura real vs. percepción del cuestionario por dimensión (la calcula el dashboard). */
export interface DimensionAnalisis {
  dimension: DimensionMadurez;
  nivelReal: NivelMadurez | null;
  nivelCuestionario: NivelMadurez | null;
}

/** Responsable y horizonte orientativos por dimensión (réplica del mapeo del Word, DIM_RESP). */
const DIM_RESP: Record<DimensionMadurez, { responsable: string; horizonte: string }> = {
  ALINEACION_ESTRATEGICA: { responsable: 'Dirección + PMO', horizonte: '0–3 meses' },
  CAPACIDAD_EVOLUCION: { responsable: 'Responsable de gobernanza', horizonte: '0–3 meses' },
  CAPACIDAD_CONTROL: { responsable: 'Oficina de Organización / RR.HH.', horizonte: '1–4 meses' },
  GOBERNABILIDAD: { responsable: 'Secretaría de gobierno / Dirección', horizonte: '3–6 meses' },
  FORMALIZACION: { responsable: 'RR.HH. / Oficina de Organización', horizonte: '3–6 meses' },
};

const NIVEL_NUM_ANALISIS: Record<NivelMadurez, number> = {
  M_0: 0,
  M_1: 1,
  M_2: 2,
  M_3: 3,
  M_4: 4,
  M_5: 5,
};

/** Primer párrafo de texto (no encabezado/viñeta) del diagnóstico → resumen ejecutivo. */
function primerParrafo(md: string): string {
  for (const linea of md.split('\n')) {
    const l = linea.trim();
    if (!l || l.startsWith('#') || l.startsWith('-') || l.startsWith('*') || l.startsWith('>'))
      continue;
    return l.replace(/\*\*/g, '');
  }
  return md.slice(0, 280).replace(/\*\*/g, '');
}

/** DAFO derivada (P0) de lo que el análisis ya produce; P1 la genera la IA. */
function derivarDafo(
  analisis: AnalisisMadurez,
  dimLabel: (d: DimensionMadurez) => string,
): DafoData {
  const dimsDebiles = [...new Set(analisis.recomendaciones.map((r) => r.dimension))];
  return {
    fortalezas: analisis.fortalezas ?? [],
    amenazas: analisis.riesgos ?? [],
    debilidades: dimsDebiles.map((d) => `Área a reforzar: ${dimLabel(d)}`),
    oportunidades: [...analisis.recomendaciones]
      .sort((a, b) => a.prioridad - b.prioridad)
      .slice(0, 5)
      .map((r) => r.textoFinal),
  };
}

const A1 = '#1F3864';
const A2 = '#2F5496';
const CYAN = '#33BCE1';
const GO = '#595959';
const GM = '#D9D9D9';
const W = '#FFF';
const VERDE = '#16A34A';
const AMBAR = '#E0A800';
const ROJO = '#DC2626';

const estadoColor: Record<string, string> = { BORRADOR: GO, GENERADO: A2, VALIDADO: VERDE };

interface Props {
  evaluacionId: string;
  /** Conclusión del ingeniero (solo lectura aquí; la edición vive en el dashboard). */
  observacionesIniciales: string | null;
  dimLabel: (d: DimensionMadurez) => string;
  /** Lectura real vs. cuestionario por dimensión (sección de discrepancias). */
  dimensiones?: DimensionAnalisis[];
  onChanged?: () => void;
}

export function MadurezAnalisisIA({
  evaluacionId,
  observacionesIniciales,
  dimLabel,
  dimensiones,
  onChanged,
}: Props) {
  const { t } = useI18n();
  const s = useCallback(
    (k: string, fb: string): string => {
      const mk = ((t.structure as Record<string, unknown>)?.madurez ?? {}) as Record<
        string,
        unknown
      >;
      return typeof mk[k] === 'string' ? (mk[k] as string) : fb;
    },
    [t],
  );

  const [analisis, setAnalisis] = useState<AnalisisMadurez | null>(null);
  // Conclusión del ingeniero: solo LECTURA aquí (la edición vive en el dashboard).
  // Se usa para mostrarla en el resumen ejecutivo del análisis.
  const observaciones = observacionesIniciales ?? '';
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [descPy, setDescPy] = useState(false);
  const [descMp, setDescMp] = useState(false);
  const [verHtml, setVerHtml] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fuente de la lectura que analiza la IA: cuestionario (criterio del ingeniero,
  // por defecto), real (organigrama) o ambas (resalta la brecha).
  const [fuente, setFuente] = useState<FuenteAnalisisMadurez>('CUESTIONARIO');
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const busy = generating || validating || descargando || descPy || descMp || verHtml;

  useEffect(() => {
    let activo = true;
    setLoading(true);
    obtenerAnalisisMadurez(evaluacionId)
      .then((res) => {
        if (activo) {
          setAnalisis(res.data);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (activo) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      activo = false;
    };
  }, [evaluacionId]);

  const generar = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await generarAnalisisMadurez(evaluacionId, fuente);
      if (!alive.current) return;
      setAnalisis(data);
      onChanged?.();
    } catch (err) {
      if (alive.current) setError(mensajeErrorIA(err, s));
    } finally {
      if (alive.current) setGenerating(false);
    }
  }, [evaluacionId, fuente, onChanged, s]);

  const validar = useCallback(async () => {
    if (!analisis) return;
    setValidating(true);
    setError(null);
    try {
      const { data } = await validarAnalisisMadurez(analisis.id);
      if (!alive.current) return;
      setAnalisis(data);
      onChanged?.();
    } catch (err) {
      if (alive.current) setError(mensajeErrorIA(err, s));
    } finally {
      if (alive.current) setValidating(false);
    }
  }, [analisis, onChanged, s]);

  const descargarInforme = useCallback(async () => {
    if (!analisis) return;
    setDescargando(true);
    setError(null);
    try {
      // Descarga binaria: fetch manual con Bearer (request() parsea JSON).
      const token = getToken();
      const res = await fetch(`${BASE}/structure/madurez/analisis/${analisis.id}/informe`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        throw new Error(body?.error?.message ?? `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'informe-madurez.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (alive.current) setError((err as Error).message);
    } finally {
      if (alive.current) setDescargando(false);
    }
  }, [analisis]);

  const exportarPy = useCallback(async () => {
    if (!analisis) return;
    setDescPy(true);
    setError(null);
    try {
      await descargarExportadorPyMadurez(analisis.id);
    } catch (err) {
      if (alive.current) setError((err as Error).message);
    } finally {
      if (alive.current) setDescPy(false);
    }
  }, [analisis]);

  const exportarMegaprompt = useCallback(async () => {
    setDescMp(true);
    setError(null);
    try {
      await descargarMegapromptMadurez(evaluacionId);
    } catch (err) {
      if (alive.current) setError((err as Error).message);
    } finally {
      if (alive.current) setDescMp(false);
    }
  }, [evaluacionId]);

  const verInformeHtml = useCallback(async () => {
    setVerHtml(true);
    setError(null);
    // Abrir la pestaña SÍNCRONAMENTE dentro del gesto del clic evita el bloqueo de popups;
    // el blob se carga después del fetch autenticado.
    const win = window.open('', '_blank');
    try {
      const url = await obtenerInformeHtmlMadurez(evaluacionId);
      if (win) win.location.href = url;
      else window.open(url, '_blank');
      // Revoca tras dar tiempo a la pestaña a cargar el documento.
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      if (alive.current) setError((err as Error).message);
    } finally {
      if (alive.current) setVerHtml(false);
    }
  }, [evaluacionId]);

  const flags = analisis?.sentinelFlags ?? [];
  const tieneError = flags.some((f) => f.severity === 'ERROR');
  const tieneWarning = flags.some((f) => f.severity === 'WARNING');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      {/* ══ Análisis V·IA Governance (cabecera con descargas + controles + resultados) ══ */}
      <SectionCard
        title={s('analisisIA', 'Análisis V·IA Governance')}
        accent={COLORS.a1}
        right={
          analisis?.estado === 'VALIDADO' ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={descargarInforme} disabled={busy} style={btnMini(A1, W, busy)}>
                {descargando
                  ? s('generandoInforme', 'Generando informe…')
                  : s('descargarInforme', 'Descargar informe Word')}
              </button>
              <button onClick={exportarPy} disabled={busy} style={btnMini(A2, W, busy)}>
                {descPy
                  ? s('generandoPy', 'Generando .py…')
                  : s('descargarPy', 'Descargar exportador .py')}
              </button>
              <button
                onClick={exportarMegaprompt}
                disabled={busy}
                style={btnMini(CYAN, '#0B2545', busy)}
              >
                {descMp
                  ? s('generandoMegaprompt', 'Generando megaprompt…')
                  : s('generarMegaprompt', 'Generar megaprompt claude.ai')}
              </button>
              <button onClick={verInformeHtml} disabled={busy} style={btnMini(VERDE, W, busy)}>
                {verHtml
                  ? s('generandoHtml', 'Generando HTML…')
                  : s('verInformeHtml', 'Generar informe HTML')}
              </button>
            </div>
          ) : undefined
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          {error && (
            <div
              style={{
                background: '#FDECEC',
                border: `1px solid ${ROJO}`,
                color: '#7B0000',
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {/* ── Acción generar ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {/* Fuente: qué lectura analiza la IA (cuestionario por defecto = criterio del ingeniero) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: GO, fontWeight: 600 }}>
                {s('fuenteAnalisis', 'Analizar')}:
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                {FUENTES.map((f) => {
                  const sel = fuente === f;
                  const fb =
                    f === 'CUESTIONARIO' ? 'Cuestionario' : f === 'REAL' ? 'Real' : 'Ambas';
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFuente(f)}
                      disabled={generating}
                      title={s(
                        `fuenteHelp_${f}`,
                        f === 'CUESTIONARIO'
                          ? 'Percepción del equipo / criterio del ingeniero'
                          : f === 'REAL'
                            ? 'Lectura objetiva del organigrama'
                            : 'Compara real vs percepción y resalta la brecha',
                      )}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: sel ? `1px solid ${A1}` : `1px solid ${GM}`,
                        background: sel ? A1 : W,
                        color: sel ? W : GO,
                        cursor: generating ? 'not-allowed' : 'pointer',
                        fontFamily: "'DM Sans',sans-serif",
                      }}
                    >
                      {s(`fuente_${f}`, fb)}
                    </button>
                  );
                })}
              </div>
            </div>
            <button onClick={generar} disabled={busy} style={btnStyle(A1, W, busy)}>
              {generating
                ? s('generando', 'Generando análisis…')
                : analisis
                  ? s('regenerar', 'Regenerar análisis IA')
                  : s('generarAnalisis', 'Generar análisis IA')}
            </button>
            {analisis && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: 12,
                  background: '#EEF',
                  color: estadoColor[analisis.estado] ?? GO,
                }}
              >
                {analisis.estado}
              </span>
            )}
            {analisis?.fuente && (
              <span style={{ fontSize: 11, color: GO }}>
                {s('fuenteAnalisis', 'Analizar')}:{' '}
                <strong style={{ color: A2 }}>
                  {s(
                    `fuente_${analisis.fuente}`,
                    analisis.fuente === 'CUESTIONARIO'
                      ? 'Cuestionario'
                      : analisis.fuente === 'REAL'
                        ? 'Real'
                        : 'Ambas',
                  )}
                </strong>
              </span>
            )}
            {analisis?.proveedor && (
              <span style={{ fontSize: 11, color: GO }}>
                {s('via', 'V·IA')}: {analisis.proveedor} ·{' '}
                {analisis.tokensEntrada + analisis.tokensSalida} tok
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ color: GO, fontSize: 12, fontStyle: 'italic' }}>
              {s('cargando', 'Cargando…')}
            </div>
          ) : !analisis ? (
            <div style={{ color: GO, fontSize: 12, fontStyle: 'italic' }}>
              {s(
                'sinAnalisis',
                'Aún no hay análisis IA. Genera uno para obtener diagnóstico y recomendaciones.',
              )}
            </div>
          ) : (
            <>
              {/* ── Sentinel ── */}
              {(tieneError || tieneWarning) && (
                <div
                  style={{
                    background: tieneError ? '#FDECEC' : '#FFF8E6',
                    border: `1px solid ${tieneError ? ROJO : AMBAR}`,
                    color: tieneError ? '#7B0000' : '#7A5C00',
                    borderRadius: 6,
                    padding: '8px 12px',
                    fontSize: 12,
                  }}
                >
                  <strong>{s('sentinel', 'Sentinel')}:</strong>{' '}
                  {tieneError
                    ? s(
                        'sentinelError',
                        'alertas críticas — el análisis no puede validarse hasta resolverlas.',
                      )
                    : s('sentinelWarn', 'avisos a revisar.')}
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {flags.slice(0, 5).map((f, i) => (
                      <li key={i}>{f.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── Resumen ejecutivo ── */}
              <SectionCard
                title={s('resumenEjecutivo', 'Resumen ejecutivo')}
                accent={COLORS.cyan}
                right={
                  analisis.estado === 'VALIDADO' ? (
                    <Badge tono="verde">{s('validado', 'Validado')}</Badge>
                  ) : tieneError ? (
                    <Badge tono="rojo">{s('requiereRevision', 'Requiere revisión')}</Badge>
                  ) : (
                    <Badge tono="azul">{s('borradorIa', 'Borrador IA')}</Badge>
                  )
                }
              >
                {observaciones.trim() && (
                  <p
                    style={{
                      fontSize: 13,
                      color: COLORS.ink,
                      lineHeight: 1.55,
                      margin: '0 0 8px',
                      borderLeft: `3px solid ${COLORS.cyan}`,
                      paddingLeft: 10,
                    }}
                  >
                    <strong style={{ color: COLORS.a1 }}>
                      {s('lecturaConsultor', 'Lectura del consultor')}:{' '}
                    </strong>
                    {observaciones.trim()}
                  </p>
                )}
                <p style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.55, margin: 0 }}>
                  {analisis.conclusionEjecutiva || primerParrafo(analisis.diagnosticoNarrativo)}
                </p>
                {analisis.recomendaciones.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {[...analisis.recomendaciones]
                      .sort((a, b) => a.prioridad - b.prioridad)
                      .slice(0, 3)
                      .map((r, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 11,
                            padding: '3px 9px',
                            borderRadius: 12,
                            background: COLORS.gc,
                            color: COLORS.a1,
                            fontWeight: 600,
                          }}
                        >
                          #{r.prioridad} {dimLabel(r.dimension)}
                        </span>
                      ))}
                  </div>
                )}
              </SectionCard>

              {/* ── Matriz DAFO (IA si está, si no derivada) ── */}
              <SectionCard title={s('dafo', 'Matriz DAFO')} accent={COLORS.a2}>
                <DAFOMatrix data={analisis.matrizDafo ?? derivarDafo(analisis, dimLabel)} />
              </SectionCard>

              {/* ── Diagnóstico ── */}
              <SectionCard title={s('diagnostico', 'Diagnóstico')} accent={COLORS.a1}>
                <Markdown text={analisis.diagnosticoNarrativo} />
              </SectionCard>

              {/* ── Recomendaciones ── */}
              {analisis.recomendaciones.length > 0 && (
                <SectionCard
                  title={s('recomendaciones', 'Recomendaciones')}
                  accent={COLORS.verde}
                  hint={String(analisis.recomendaciones.length)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...analisis.recomendaciones]
                      .sort((a, b) => a.prioridad - b.prioridad)
                      .map((r, i) => (
                        <div
                          key={i}
                          style={{
                            border: `1px solid ${COLORS.gm}`,
                            borderLeft: `3px solid ${COLORS.a2}`,
                            borderRadius: 8,
                            padding: '10px 12px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: COLORS.w,
                                background: COLORS.a1,
                                borderRadius: 10,
                                padding: '1px 8px',
                              }}
                            >
                              #{r.prioridad}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 800, color: COLORS.a2 }}>
                              {dimLabel(r.dimension)}
                            </span>
                            <Badge tono={r.noAnclada ? 'ambar' : 'verde'}>
                              {r.noAnclada
                                ? s('noAnclada', 'propuesta IA')
                                : s('anclada', 'anclada al catálogo')}
                            </Badge>
                            {r.cuadrantePick && PICK_BADGE[r.cuadrantePick] && (
                              <Badge tono={PICK_BADGE[r.cuadrantePick].tono}>
                                {s(`pick_${r.cuadrantePick}`, PICK_BADGE[r.cuadrantePick].label)}
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5 }}>
                            {r.textoFinal}
                          </div>
                          {r.justificacion && (
                            <div
                              style={{
                                fontSize: 11,
                                color: COLORS.go,
                                marginTop: 4,
                                fontStyle: 'italic',
                                lineHeight: 1.45,
                              }}
                            >
                              {r.justificacion}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </SectionCard>
              )}

              {/* ── Hoja de ruta (recomendaciones priorizadas + responsable/horizonte) ── */}
              {analisis.recomendaciones.length > 0 && (
                <SectionCard
                  title={s('hojaRuta', 'Hoja de ruta')}
                  accent={COLORS.cyan}
                  hint={String(analisis.recomendaciones.length)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...analisis.recomendaciones]
                      .sort((a, b) => a.prioridad - b.prioridad)
                      .map((r, i) => {
                        const resp = DIM_RESP[r.dimension];
                        return (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 10,
                              border: `1px solid ${COLORS.gm}`,
                              borderLeft: `3px solid ${COLORS.cyan}`,
                              borderRadius: 8,
                              padding: '10px 12px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: COLORS.w,
                                background: COLORS.a1,
                                borderRadius: 10,
                                padding: '1px 8px',
                                flexShrink: 0,
                                marginTop: 1,
                              }}
                            >
                              #{r.prioridad}
                            </span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  marginBottom: 3,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span style={{ fontSize: 11, fontWeight: 800, color: COLORS.a2 }}>
                                  {dimLabel(r.dimension)}
                                </span>
                                {r.cuadrantePick && PICK_BADGE[r.cuadrantePick] && (
                                  <Badge tono={PICK_BADGE[r.cuadrantePick].tono}>
                                    {s(
                                      `pick_${r.cuadrantePick}`,
                                      PICK_BADGE[r.cuadrantePick].label,
                                    )}
                                  </Badge>
                                )}
                              </div>
                              <div style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5 }}>
                                {r.textoFinal}
                              </div>
                              {resp && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: COLORS.go,
                                    marginTop: 5,
                                    display: 'flex',
                                    gap: 12,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span>
                                    <strong style={{ color: COLORS.a1 }}>
                                      {s('responsable', 'Responsable')}:
                                    </strong>{' '}
                                    {resp.responsable}
                                  </span>
                                  <span>
                                    <strong style={{ color: COLORS.a1 }}>
                                      {s('horizonte', 'Horizonte')}:
                                    </strong>{' '}
                                    {resp.horizonte}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </SectionCard>
              )}

              {/* ── Fortalezas / Riesgos ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <SectionCard title={s('fortalezas', 'Fortalezas')} accent={COLORS.verde}>
                  <ListaPunteada color={COLORS.verde} items={analisis.fortalezas} />
                </SectionCard>
                <SectionCard title={s('riesgos', 'Riesgos')} accent={COLORS.rojo}>
                  <ListaPunteada color={COLORS.rojo} items={analisis.riesgos} />
                </SectionCard>
              </div>

              {/* ── Discrepancias realidad ↔ cuestionario (reemplaza el panel de varianza) ── */}
              {dimensiones && dimensiones.length > 0 && (
                <SectionCard
                  title={s('discrepanciaRealCuest', 'Discrepancias realidad ↔ cuestionario')}
                  accent={COLORS.a2}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    <thead>
                      <tr>
                        {[
                          s('colDimension', 'Dimensión'),
                          s('colReal', 'Real'),
                          s('colPercepcion', 'Percepción'),
                          s('colLectura', 'Lectura'),
                        ].map((h, i) => (
                          <th
                            key={i}
                            style={{
                              textAlign: i === 0 || i === 3 ? 'left' : 'center',
                              padding: '6px 8px',
                              fontSize: 10,
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              color: GO,
                              borderBottom: `1px solid ${GM}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dimensiones.map((d) => {
                        const real = d.nivelReal ? NIVEL_NUM_ANALISIS[d.nivelReal] : null;
                        const perc = d.nivelCuestionario
                          ? NIVEL_NUM_ANALISIS[d.nivelCuestionario]
                          : null;
                        let lectura: string;
                        let tono: { bg: string; fg: string };
                        if (perc == null || real == null) {
                          lectura = s('lecturaSinDato', 'Sin dato de cuestionario');
                          tono = { bg: COLORS.gc, fg: GO };
                        } else if (real === perc) {
                          lectura = s('lecturaCoincide', 'Coincide');
                          tono = { bg: COLORS.gc, fg: GO };
                        } else if (real > perc) {
                          lectura = s(
                            'lecturaInfravalora',
                            'Se infravalora: la realidad supera la percepción',
                          ).replace('{n}', String(real - perc));
                          tono = { bg: '#EAF1FB', fg: A1 };
                        } else {
                          lectura = s(
                            'lecturaSobreestima',
                            'Se sobreestima: se percibe más madura de lo real',
                          ).replace('{n}', String(perc - real));
                          tono = { bg: '#FFF8E6', fg: '#7A5C00' };
                        }
                        const delta =
                          real != null && perc != null && real !== perc
                            ? `${real > perc ? '+' : '−'}${Math.abs(real - perc)}`
                            : '';
                        return (
                          <tr key={d.dimension}>
                            <td
                              style={{
                                padding: '6px 8px',
                                fontSize: 12,
                                fontWeight: 600,
                                color: COLORS.a1,
                                borderBottom: `1px solid ${COLORS.gc}`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {dimLabel(d.dimension)}
                            </td>
                            <td
                              style={{
                                padding: '6px 8px',
                                fontSize: 12,
                                textAlign: 'center',
                                color: COLORS.ink,
                                borderBottom: `1px solid ${COLORS.gc}`,
                              }}
                            >
                              {d.nivelReal ? d.nivelReal.replace('_', '-') : '—'}
                            </td>
                            <td
                              style={{
                                padding: '6px 8px',
                                fontSize: 12,
                                textAlign: 'center',
                                color: COLORS.ink,
                                borderBottom: `1px solid ${COLORS.gc}`,
                              }}
                            >
                              {d.nivelCuestionario ? d.nivelCuestionario.replace('_', '-') : '—'}
                            </td>
                            <td
                              style={{
                                padding: '6px 8px',
                                borderBottom: `1px solid ${COLORS.gc}`,
                              }}
                            >
                              <span
                                style={{
                                  display: 'inline-block',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  background: tono.bg,
                                  color: tono.fg,
                                }}
                              >
                                {delta ? `${lectura} (${delta})` : lectura}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </SectionCard>
              )}

              {/* ── Validar ── */}
              {analisis.estado === 'GENERADO' && (
                <div>
                  <button
                    onClick={validar}
                    disabled={busy || tieneError}
                    style={btnStyle(VERDE, W, busy || tieneError)}
                  >
                    {s('validar', 'Validar análisis')}
                  </button>
                  {tieneError && (
                    <span style={{ marginLeft: 10, fontSize: 11, color: ROJO }}>
                      {s('bloqueadoSentinel', 'Bloqueado por Sentinel')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function btnStyle(bg: string, color: string, disabled: boolean): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 700,
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: bg,
    color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: "'DM Sans',sans-serif",
  };
}

/** Botón compacto/ligero para la cabecera del card (menos padding, fontSize 11). */
function btnMini(bg: string, color: string, disabled: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 6,
    border: 'none',
    background: bg,
    color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: "'DM Sans',sans-serif",
  };
}
